import { Badge, Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import {
    AttractionReadinessLevelEnum,
    AttractionReadinessSnapshot,
    attractionReadinessGaps,
    attractionReadinessLayers,
} from '@routellers/types/dist/city-attraction/attraction-readiness';
import { useTranslation } from 'react-i18next';

import { enumLabel } from '../i18n';

const LEVEL_COLOR: Record<AttractionReadinessLevelEnum, string> = {
    [AttractionReadinessLevelEnum.NONE]: 'gray',
    [AttractionReadinessLevelEnum.TRIAGED]: 'gray',
    [AttractionReadinessLevelEnum.PASSPORT]: 'cyan',
    [AttractionReadinessLevelEnum.SCOUTED]: 'grape',
    [AttractionReadinessLevelEnum.LIGHT]: 'lime',
    [AttractionReadinessLevelEnum.BASE]: 'blue',
    [AttractionReadinessLevelEnum.BRIEF]: 'indigo',
    [AttractionReadinessLevelEnum.GUIDE]: 'teal',
    [AttractionReadinessLevelEnum.AUDIO]: 'green',
};

export const AttractionReadinessBadge = ({
    readiness,
    withNext = false,
}: {
    readiness?: AttractionReadinessSnapshot | null;
    withNext?: boolean;
}) => {
    const { t } = useTranslation();

    if (!readiness) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }

    const gaps = attractionReadinessGaps(readiness);
    const missing = gaps
        .map((key) => enumLabel(t, 'enums.attractionReadinessCheck', key))
        .join(', ');
    const hint = gaps.length
        ? t('pages.attractions.readinessNext', { items: missing })
        : t('pages.attractions.readinessDone');

    return (
        <Tooltip label={hint} multiline maw={280}>
            <Badge
                color={LEVEL_COLOR[readiness.level] || 'gray'}
                variant="light"
                style={{ cursor: 'default' }}
            >
                {withNext && gaps.length
                    ? `${enumLabel(t, 'enums.attractionReadiness', readiness.level)} · ${missing}`
                    : enumLabel(t, 'enums.attractionReadiness', readiness.level)}
            </Badge>
        </Tooltip>
    );
};

/** Step track for the attractions list: level name stays readable, gaps sit under it. */
export const AttractionReadinessProgress = ({
    readiness,
}: {
    readiness?: AttractionReadinessSnapshot | null;
}) => {
    const { t } = useTranslation();

    if (!readiness) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }

    const path = attractionReadinessLayers(readiness.tier);
    const targetIndex = readiness.target
        ? path.findIndex((layer) => layer.level === readiness.target)
        : -1;
    const layers = targetIndex >= 0 ? path.slice(0, targetIndex + 1) : path;
    const checks = readiness.checks || {};
    let reached = -1;

    for (let index = 0; index < layers.length; index += 1) {
        const done = layers[index].checks.every((key) => checks[key]);

        if (!done) break;
        reached = index;
    }
    const gaps = attractionReadinessGaps(readiness);
    const missing = gaps
        .map((key) => enumLabel(t, 'enums.attractionReadinessCheck', key))
        .join(', ');
    const hint = gaps.length
        ? t('pages.attractions.readinessNext', { items: missing })
        : t('pages.attractions.readinessDone');
    const color = LEVEL_COLOR[readiness.level] || 'gray';

    return (
        <Tooltip label={hint} multiline maw={280}>
            <Stack gap={4}>
                <Group gap={3} wrap="nowrap">
                    {layers.map((layer, index) => (
                        <Box
                            key={layer.level}
                            h={8}
                            style={{
                                width: 18,
                                borderRadius: 99,
                                background:
                                    index <= reached
                                        ? `var(--mantine-color-${color}-6)`
                                        : 'var(--mantine-color-gray-4)',
                            }}
                        />
                    ))}
                </Group>
                <Text size="xs" fw={600} lh={1.2}>
                    {enumLabel(t, 'enums.attractionReadiness', readiness.level)}
                    {' · '}
                    {reached + 1}/{layers.length}
                </Text>
                <Text size="xs" c="dimmed" lh={1.2} lineClamp={2}>
                    {hint}
                </Text>
            </Stack>
        </Tooltip>
    );
};
